import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiBarChart2, FiPlus, FiTrash2, FiX, FiDownload } = FiIcons;

export default function PollsModerator() {
  const [polls, setPolls] = useState([]);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [loading, setLoading] = useState(false);
  const [votesCount, setVotesCount] = useState({});

  useEffect(() => {
    fetchPollsAndVotes();
    const channel = supabase
      .channel('polls_realtime_dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gallo_polls_1786733905424' }, fetchPollsAndVotes)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gallo_poll_votes_1786733905424' }, fetchPollsAndVotes)
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchPollsAndVotes = async () => {
    const { data: pollData } = await supabase
      .from('gallo_polls_1786733905424')
      .select('*')
      .order('created_at', { ascending: false });

    if (pollData) {
      setPolls(pollData);
      const { data: voteData } = await supabase
        .from('gallo_poll_votes_1786733905424')
        .select('poll_id, option_index');

      if (voteData) {
        const counts = {};
        voteData.forEach((vote) => {
          if (!counts[vote.poll_id]) counts[vote.poll_id] = {};
          counts[vote.poll_id][vote.option_index] = (counts[vote.poll_id][vote.option_index] || 0) + 1;
        });
        setVotesCount(counts);
      }
    }
  };

  const handleAddOption = () => {
    if (options.length < 5) setOptions([...options, '']);
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) setOptions(options.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index, value) => {
    const next = [...options];
    next[index] = value;
    setOptions(next);
  };

  const handleCreatePoll = async (e) => {
    e.preventDefault();
    if (!question.trim() || options.some((o) => !o.trim())) return;
    setLoading(true);

    await supabase.from('gallo_polls_1786733905424').update({ is_active: false }).eq('is_active', true);

    const { error } = await supabase.from('gallo_polls_1786733905424').insert({
      question: question.trim(),
      options: options.filter((o) => o.trim() !== ''),
      is_active: true
    });

    if (!error) {
      setQuestion('');
      setOptions(['', '']);
      fetchPollsAndVotes();
    }
    setLoading(false);
  };

  const handleDeletePoll = async (id) => {
    if (window.confirm('¿Seguro que deseas eliminar esta encuesta?')) {
      await supabase.from('gallo_polls_1786733905424').delete().eq('id', id);
      fetchPollsAndVotes();
    }
  };

  const handleTogglePollStatus = async (id, currentStatus) => {
    await supabase.from('gallo_polls_1786733905424').update({ is_active: !currentStatus }).eq('id', id);
    fetchPollsAndVotes();
  };

  const handleExportCSV = (poll) => {
    const pollVotes = votesCount[poll.id] || {};
    const totalVotes = Object.values(pollVotes).reduce((a, b) => a + b, 0);
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += `Pregunta: "${poll.question.replace(/"/g, '""')}"\n`;
    csvContent += `Fecha: "${new Date(poll.created_at).toLocaleString()}"\n`;
    csvContent += `Total de Votos: ${totalVotes}\n\n`;
    csvContent += 'Opcion,Votos,Porcentaje\n';

    poll.options.forEach((opt, idx) => {
      const votes = pollVotes[idx] || 0;
      const percent = totalVotes > 0 ? ((votes / totalVotes) * 100).toFixed(1) : '0.0';
      csvContent += `"${opt.replace(/"/g, '""')}",${votes},${percent}%\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `encuesta_${poll.id.slice(0, 6)}_resultados.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#111] p-5 space-y-6">
      <div>
        <h3 className="font-semibold text-white text-sm sm:text-base flex items-center gap-2">
          <SafeIcon icon={FiBarChart2} className="text-red-500" /> Encuestas en Tiempo Real
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Crea encuestas interactivas que aparecerán en la pantalla de los espectadores para que voten en vivo.
        </p>
      </div>

      <form onSubmit={handleCreatePoll} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5">Pregunta de la Encuesta</label>
          <input
            type="text"
            required
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="¿Quién ganará el próximo combate?"
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs sm:text-sm text-white focus:border-red-600 focus:outline-none"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-400">Opciones de Respuesta</label>
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                required
                value={opt}
                onChange={(e) => handleOptionChange(i, e.target.value)}
                placeholder={`Opción ${i + 1}`}
                className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs text-white focus:border-red-600 focus:outline-none"
              />
              {options.length > 2 && (
                <button
                  type="button"
                  onClick={() => handleRemoveOption(i)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} size={14} />
                </button>
              )}
            </div>
          ))}

          {options.length < 5 && (
            <button
              type="button"
              onClick={handleAddOption}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-400 transition-colors pt-1"
            >
              <SafeIcon icon={FiPlus} size={14} /> Añadir opción
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !question.trim()}
          className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs sm:text-sm transition-all disabled:opacity-50"
        >
          {loading ? 'Creando...' : 'Lanzar Encuesta En Vivo'}
        </button>
      </form>

      <div className="border-t border-white/5 pt-5 space-y-3">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Historial de Encuestas & Exportación</h4>
        <div className="space-y-3 max-h-[350px] overflow-y-auto">
          {polls.map((poll) => {
            const pollVotes = votesCount[poll.id] || {};
            const totalVotes = Object.values(pollVotes).reduce((a, b) => a + b, 0);
            return (
              <div key={poll.id} className="p-3 bg-black/40 border border-white/5 rounded-xl space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-bold text-white">{poll.question}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{totalVotes} votos totales</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => handleExportCSV(poll)}
                      title="Exportar a CSV"
                      className="p-1.5 text-gray-400 hover:text-white rounded bg-white/5 hover:bg-white/10 transition-all flex items-center gap-1 text-[10px] font-semibold"
                    >
                      <SafeIcon icon={FiDownload} size={12} /> CSV
                    </button>
                    <button
                      onClick={() => handleTogglePollStatus(poll.id, poll.is_active)}
                      className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                        poll.is_active ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'
                      }`}
                    >
                      {poll.is_active ? 'Activa' : 'Pausada'}
                    </button>
                    <button
                      onClick={() => handleDeletePoll(poll.id)}
                      className="p-1 text-gray-500 hover:text-red-500 rounded hover:bg-white/5 transition-all"
                    >
                      <SafeIcon icon={FiTrash2} size={12} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5 pt-1">
                  {poll.options.map((opt, idx) => {
                    const votes = pollVotes[idx] || 0;
                    const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
                    return (
                      <div key={idx} className="space-y-0.5">
                        <div className="flex justify-between text-[10px] text-gray-400">
                          <span>{opt}</span>
                          <span>{votes} ({percent}%)</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-red-600 rounded-full" style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {polls.length === 0 && (
            <p className="text-center text-xs text-gray-600 py-4">No hay encuestas creadas aún.</p>
          )}
        </div>
      </div>
    </section>
  );
}